import { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Input, Modal, message } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { useGetChildStatusReasonTypesQuery, useGetChildStatusTypesQuery } from '@/services/general-api';
import {
  useUpdateChildStatusMutation,
  usePlaceWithParentsMutation,
  useSetAdoptionsMutation
} from '@/services/child-api';
import Select from '@/components/select';
import ParentQuickSearch from './parent-quick-search';

const REMOVED_CODE = 'removed';
const MONITORING_CODE = 'monitoring';
const CHLDAFIN = 'CHLDAFIN';
const CHILD_HAS = 'CPIPAH';
const CHILD_WAS = ' CHILD WAS';
const FAMILIES_A = 'FAMILIES A';
const monitoringReasonCodes = [CHILD_HAS, CHILD_WAS, FAMILIES_A];

const CHILD_ADOPTION_TYPES = [
  { id: 21110, name: 'Guardianship' },
  { id: 21120, name: 'Adopted - IAP Recommended Family' },
  { id: 21130, name: 'Adopted - Out Of State' },
  { id: 21140, name: 'Adopted - Current Placement / non-IAP Recommended Family' },
  { id: 21150, name: 'Adopted - Kinship / Relative' },
];

const StatusModal = ({ childId, open, onSave, onClose, childSummaryFileName }) => {
  const { data: statusData, isLoading: loadingStatusData } = useGetChildStatusTypesQuery();
  const filteredStatusData = statusData && statusData.filter((item) => item.name !== 'All');
  const { data: reasonData, isLoading: loadingReasonData } = useGetChildStatusReasonTypesQuery();
  const [removeReasonData, monitoringReasonData] = useMemo(
    () => [
      reasonData?.filter(({ code }) => !monitoringReasonCodes.includes(code)),
      reasonData?.filter(({ code }) => monitoringReasonCodes.includes(code)),
    ],
    [reasonData],
  );
  const [updateStatus, { isLoading: updating }] = useUpdateChildStatusMutation();
  const [placeWithParents, { isLoading: placing }] = usePlaceWithParentsMutation();
  const [setAdoptions, { isLoading: settingAdoption }] = useSetAdoptionsMutation();

  const [reason, setReason] = useState(undefined);
  const [additionalFields, setAdditionalFields] = useState(null);

  const [form] = Form.useForm();
  let selectedParent = {};

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        form.resetFields();
        setReason(undefined);
        setAdditionalFields(null);
      });
    }
  }, [open, form]);

  useEffect(() => {
    if (reason) {
      const reasonDetails = _.find(reasonData, { id: reason });
      if (!reasonDetails) return;

      switch (reasonDetails.code) {
        case CHLDAFIN:
          setAdditionalFields('adoption');
          break;
        case CHILD_HAS:
        case CHILD_WAS:
          setAdditionalFields('preAdoptive');
          break;
        default:
          setAdditionalFields('normal');
      }
    } else {
      setAdditionalFields(null);
    }
  }, [reason, reasonData]);

  const onFinish = async (values) => {
    values.childId = childId;
    values.date = values.date?.format('YYYY-MM-DD');
    values.notes = values.notes || {};
    values.parent = { ...values.parent, ...selectedParent };

    const selectedStatusId = Number(values?.status?.id);
    const hasSummaryFile = !!childSummaryFileName?.trim();

    if (selectedStatusId === 1010 && !hasSummaryFile) {
      message.error('A child summary must be uploaded before setting this status.');
      return;
    }

    if (selectedStatusId === 1030 && !hasSummaryFile) {
      message.error('A child summary must be uploaded before setting this status.');
      return;
    }

    let reasonText = '';
    if (values?.reason?.id) reasonText = _.find(reasonData, { id: values.reason.id }).name;
    try {
      if (JSON.stringify(values.notes) !== '{}' && reasonText === "Child's adoption was finalized") {
        values.parent = { ...values.parent, parentNotes: values.notes.notes };
        values.notes = {};
      }
      await updateStatus(values).unwrap();
      if (reasonText === 'Child has been placed in pre-adoptive home')
        await placeWithParents({ childId, parentId: selectedParent?.parentId, note: values.notes.notes, datePlaced: values.datePlaced }).unwrap();
      else if (reasonText === 'Child was matched with a pre-adoptive home')
        await placeWithParents({ childId, parentId: selectedParent?.parentId, note: values.notes.notes, dateMatched: values.datePlaced }).unwrap();
      else if (JSON.stringify(values.parent) !== '{}' && reasonText === "Child's adoption was finalized") {
        const data = {
          id: parseInt(values.status.id, 10),
          childId: parseInt(childId, 10),
          AdoptionDate: values.date,
          parentId: selectedParent.parentId,
          note: values.parent.parentNotes,
          type: { id: parseInt(values.adoption.type, 10) },
        };
        try {
          await setAdoptions(data).unwrap();
        } catch (err) {
          message.error({
            content: err.data.message,
            duration: 9, // duration in seconds
          });
        }
      }
      await onSave();
    } catch (err) { }
    onClose();
  };

  const handleSelected = (parentId, value) => {
    selectedParent = { parentId: parentId, searchText: value };
  };

  const renderAdditionalFields = () => {
    if (additionalFields === 'adoption') {
      return (
        <>
          <Form.Item name="date" label="Date of Adoption" rules={[{ required: true }]}>
            <DatePicker
              format="MM/DD/YYYY"
              // value={date}
              onBlur={(e) => {
                const inputVal = e.target.value;
                const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                if (validDate.isValid()) {
                  form.setFieldsValue({
                    date: validDate,
                  });
                  // setDate(dateToString(validDate));
                } else {
                  form.setFieldsValue({
                    date: null,
                  });
                }
              }}
            // onChange={(day) => {
            //   setDate(dateToString(day));
            // }}
            />
          </Form.Item>
          <Form.Item name={['parent', 'searchText']} label="Adopting Parent">
            {/* <Input placeholder="Parent Name" /> */}
            <ParentQuickSearch onParentSelect={handleSelected} />
          </Form.Item>
          <Form.Item name={['notes', 'notes']} label="Notes">
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
          <Form.Item name={['adoption', 'type']} label="Adoption Type" rules={[{ required: true }]}>
            <Select options={CHILD_ADOPTION_TYPES} />
          </Form.Item>
        </>
      );
    }
    if (additionalFields === 'preAdoptive') {
      return (
        <>
          <Form.Item name="datePlaced" label="Date" rules={[{ required: true }]}>
            <DatePicker
              format="MM/DD/YYYY"
              // value={date}
              onBlur={(e) => {
                const inputVal = e.target.value;
                const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                if (validDate.isValid()) {
                  form.setFieldsValue({
                    date: validDate,
                  });
                } else {
                  form.setFieldsValue({
                    date: null,
                  });
                }
              }}
            // onChange={(day) => {
            //   setDate(dateToString(day));
            // }}
            />
          </Form.Item>
          <Form.Item name={['parent', 'searchText']} label="Parent"rules={[{ validator: (_, value) => 
                (selectedParent?.parentId || form.getFieldValue('notes')) ? Promise.resolve() : Promise.reject('Either a Parent or Note should be supplied')
              }]}>
            {/* <Input placeholder="Parent Name" /> */}
            <ParentQuickSearch onParentSelect={handleSelected} />
          </Form.Item>
          <Form.Item name={['notes', 'notes']} label="Notes">
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
        </>
      );
    }
    if (additionalFields === 'normal')
      return (
        <Form.Item name={['notes', 'notes']} label="Notes">
          <Input.TextArea autoSize={{ minRows: 3 }} />
        </Form.Item>
      );
  };

  return (
    <Modal
      title="Change Child Status"
      open={open}
      confirmLoading={updating || placing || settingAdoption}
      okText="Update"
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item name={['status', 'id']} label="Status" rules={[{ required: true }]}>
          <Select options={filteredStatusData} loading={loadingStatusData} showSearch />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.status?.id !== currentValues.status?.id}
        >
          {({ getFieldValue }) => {
            // form.setFieldValue(['reason', 'id'], undefined);
            const { code } = _.find(statusData, { id: getFieldValue('status')?.id }) || {};
            if (!code) return null;
            return code === REMOVED_CODE || code === MONITORING_CODE ? (
              <Form.Item name={['reason', 'id']} label="Reason" rules={[{ required: true }]}>
                <Select
                  options={code === REMOVED_CODE ? removeReasonData : monitoringReasonData}
                  loading={loadingReasonData}
                  onChange={(val) => {
                    setReason(val);
                  }}
                />
              </Form.Item>
            ) : (
              <Form.Item name={['notes', 'notes']} label="Notes">
                <Input.TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
            );
          }}
        </Form.Item>

        {/* {reason && (
          // <Form.Item
          //   noStyle
          //   shouldUpdate={(prevValues, currentValues) => prevValues.reason?.id !== currentValues.reason?.id}
          // >
          <>
            {() => {
              console.log('adsf');
              const { code } = _.find(reasonData, { id: reason }) || {};
              console.log(reason, code);
              if (!code) return null;
            }}
          </>
          // </Form.Item>
        )} */}
        {renderAdditionalFields()}
      </Form>
    </Modal>
  );
};

export default StatusModal;
